import { en } from '@/shared/lib/common/i18n';

/** Example questions under one heading, offered while the search box is empty. */
export type QuestionIdeaGroup = {
  heading: string;
  questions: string[];
};

type Ideas = typeof en.aiSearch.ideas;
type Topic = 'visits' | 'alerts' | 'tasks' | 'medRec' | 'careTime';

const TOPICS: Topic[] = ['visits', 'alerts', 'tasks', 'medRec', 'careTime'];

/** How many ideas the empty search box offers. */
const IDEA_COUNT = 4;

/** The topics that fit a page, first match wins. Pages with no match draw from every topic. */
const PAGE_TOPICS: [RegExp, Topic[]][] = [
  [/alert|review-patients/i, ['alerts', 'tasks']],
  [/schedule|follow-?up/i, ['visits']],
  [/chart-review|task/i, ['tasks']],
  [/med-?rec|med-history/i, ['medRec']],
  [/chronic-care|ccm/i, ['careTime', 'tasks']],
  [/remote-patient-monitoring|rpm/i, ['alerts', 'visits', 'careTime']],
  [/reports/i, ['careTime', 'medRec']]
];

const shuffle = <T>(items: readonly T[], random: () => number): T[] => {
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index--) {
    const swap = Math.floor(random() * (index + 1));
    [copy[index], copy[swap]] = [copy[swap], copy[index]];
  }
  return copy;
};

const questionsOf = (topics: Topic[], ideas: Ideas) => topics.flatMap(topic => ideas[topic]);

/**
 * A few questions to show in the empty search box, picked at random each time it opens. Most come
 * from the topics that fit the page; one comes from the rest so other kinds of question show up too.
 * On a patient's page the patient questions lead, under that patient's name when it is known.
 */
export const pickQuestionIdeas = (
  pathname: string,
  patient: { isOpen: boolean; name: string | null },
  ideas: Ideas = en.aiSearch.ideas,
  random: () => number = Math.random
): QuestionIdeaGroup[] => {
  if (patient.isOpen) {
    return [
      {
        heading: patient.name ? ideas.aboutPatient(patient.name) : ideas.aboutThisPatient,
        questions: shuffle(ideas.patient, random).slice(0, IDEA_COUNT - 1)
      },
      { heading: ideas.heading, questions: shuffle(questionsOf(TOPICS, ideas), random).slice(0, 1) }
    ];
  }

  const fitting = PAGE_TOPICS.find(([pattern]) => pattern.test(pathname))?.[1];
  if (!fitting) return [{ heading: ideas.heading, questions: shuffle(questionsOf(TOPICS, ideas), random).slice(0, IDEA_COUNT) }];

  const others = TOPICS.filter(topic => !fitting.includes(topic));
  const main = shuffle(questionsOf(fitting, ideas), random).slice(0, IDEA_COUNT - 1);
  const extra = shuffle(questionsOf(others, ideas), random).slice(0, IDEA_COUNT - main.length);
  return [{ heading: ideas.heading, questions: [...main, ...extra] }];
};
